import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    AppointmentInstructor,
    KioskAppointmentReason,
    KioskCourseOption,
    KioskCustomLanguage,
    KioskGroupSession,
    KioskService,
    SFEntityID,
    SwipeCardConfigs,
    VirtualSignInOptions
} from 'utils/types';
import { canViewWaitList, STEPS, landingPageForVirtualKiosk, keepSessionAlive } from 'utils/kiosk';
import StickyFooterLayout from 'common/StickyFooterLayout';
import ErrorMessage from 'common/ErrorMessage';
import ConfirmAppointment from './ConfirmAppointment';
import SignIn from './SignIn';
import KioskFooter from './KioskFooter';
import KioskHeader from './KioskHeader';
import WaitList from './WaitList';
import ConfirmActivity from 'common/ConfirmActivity';
import ProspectiveForm from './ProspectiveForm';
import ServiceSelection from './ServiceSelection';
import ProspectiveSignIn from './ProspectiveSignIn';
import ReasonSelection from './ReasonSelection';
import GroupSelection from './GroupSelection';
import ConfirmationPage from './ConfirmationPage';
import MoreInformation from './MoreInformation';
import StyledButton from 'common/StyledButton';
import CourseSelection from './CourseSelection';
import CheckedInOptions from './CheckedInOptions';
import css from './App.css';
import SelectMeetingType from './SelectMeetingType';
import MultipleCheckInsOption from './MultipleCheckInsOption';
import WarningMessage from 'common/WarningMessage';
import useHistory from 'hooks/useHistory';
import AuthenticatedSignIn from './AuthenticatedSignIn';

/**
 * @param {Object} props - see propTypes
 * @returns {Object} Renders the Kiosk and controls which step in the flow should be showing.
 */
function Kiosk(props) {
    const { state: step, set: setStep, replace: replaceStep, undo: goBack, clear: startOver, canUndo: canGoBack } = useHistory(props.initialStep);
    const [studentLastName, setStudentLastName] = useState("");
    const [loginId, setLoginId] = useState("");
    
    // Call keep alive API periodically to keep session active for a physical kiosk.
    !props.virtualSignInOptions.virtualSignInEnabled && keepSessionAlive();

    const contentRef = useRef();

    // The first time loading SIGN_IN, we want to clear out the ID's users will be entering
    useEffect(() => {
        if(step === STEPS.SIGN_IN) {
            setLoginId("");
            setStudentLastName("");
        }
    }, [step]);

    const authenticatedVirtualSignInForSpecificService = () => !!(props.virtualSignInOptions.virtualSignInEnabled
        && props.virtualSignInOptions.virtualServiceId
        && props.virtualSignInOptions.virtualStudentId);

    /**
     * Provides a copy of the requested item from the customLanguage prop or a blank string if it is missing.
     * @param {string} title The name of the custom language option
     * @returns {string} The value of the custom language option or a blank string
     */
    const getKioskLanguage = (title) => props.customLanguage[title] || "";

    /**
     * 1) When the student presses the "Start Over" button for physical check ins, they will be taken back 
     *    to the "Sign In" page.
     * 2) For prospective students using the virtual check in, they will be returned to the AuthenticatedSignIn page
     *    since they do not have a session we need to reset.
     * 3) In the case of a virtual check in, if the student is on the "MULTIPLE_CHECKINS_OPTION" (aka the student is
     *    already checked into another service) and chooses not to join an additional service, take them back to
     *    either the Student Dashboard or the Success Network.
     * 4) For all other virtual check in scenarios, since we are only ever checking in the signed in student for the
     *    chosen service, we by-pass the "Sign In" page and kiosk-specific timeouts. As a result, the "Start Over" 
     *    functionality using the history causes a lot of downstream bugs. The cleanest solution (both from a code and 
     *    UX standpoint) is to just reload the page completely on "Start Over" when the student is using virtual check-in.
     */
    const handleStartOver = () => {
        props.clearNotifications();
        if (props.virtualSignInOptions.virtualSignInEnabled) {
            if (props.isProspectiveStudent) startOver();
            else if (step === STEPS.MULTIPLE_CHECKINS_OPTION) window.location = landingPageForVirtualKiosk();
            else window.location.reload();
        } else {
            startOver();
        }
    }

    /**
     * This is called when a student is asked to confirm an appointment and chooses to join the waiting room queue
     * instead.
     *    1) In the case of a physical check in, the student is prompted to select the service they'd like to sign into.
     *    2) In the case of a virtual check in:
     *          a) If we already know the service the student is looking to join the queue for.
     *          b) If the student is trying to join the queue for the same service associated with the appointment, we
     *             go directly to the reason page. 
     *          c) If the student is trying to join the queue for a service not associated with the appointment, we 
     *             need to update the state to be looking at their chosen service. We also replace the history so
     *             that the student cannot press the button to go back to the confirm appointment page. Since we've
     *             changed the selected service in the state, letting them go back will cause a bunch of JS errors.
     *             Rather than creating a one-off change of the "Back" functionality for this specific edge case,
     *             we opted to remove the "Back" button completely so as to avoid additional complexity.
     */
    const handleJoinWaitingRoomFromConfirmAppointment = () => {
        props.setServiceWithAppointmentAsVisited(props.service);
        if (authenticatedVirtualSignInForSpecificService()) {
            if (props.service.serviceId === props.virtualSignInOptions.virtualServiceId) {
                setStep(STEPS.SELECT_REASON);
            } else {
                replaceStep(props.selectService(
                    props.services.filter(service => service.serviceId === props.virtualSignInOptions.virtualServiceId)[0],
                    true));
            }
        } else {
            setStep(STEPS.SELECT_SERVICE);
        }
    }

    const getStep = () => {
        switch (step) {
            case STEPS.SIGN_IN:
                return (
                    <SignIn
                        onSignIn={(signInJson, isProspective) => setStep(props.processSignIn(signInJson, isProspective))}
                        setError={props.setError}
                        kioskId={props.kioskId}
                        kioskName={props.kioskName}
                        kioskImageUrl={props.kioskImageUrl}
                        greetingText={getKioskLanguage("swipePageHeaderOne")}
                        mainHeader={getKioskLanguage("swipePageHeaderTwo")}
                        instructionsText={getKioskLanguage("swipePageInstructions")}
                        prospectiveStudentsText={getKioskLanguage("prospectiveStudentsPageHeaderTwo")}
                        lastNameSignOnDisabled={props.lastNameSignOnDisabled}
                        studentIdField={props.studentIdField}
                        lastNameField={props.lastNameField}
                        showToProspectiveStudents={props.showToProspectiveStudents}
                        goToProspectiveForm={() => setStep(STEPS.PROSPECTIVE_FORM)}
                        setStudentLastName={setStudentLastName}
                        studentLastName={studentLastName}
                        setLoginId={setLoginId}
                        loginId={loginId}
                        setWarning={props.setWarning}
                        swipeCardConfigs={props.swipeCardConfigs}
                        loadActiveXListener={props.loadActiveXListener}
                        unloadActiveXListener={props.unloadActiveXListener}
                        updateActiveXLastName={props.updateActiveXLastName} />
                );
            case STEPS.AUTHENTICATED_SIGN_IN:
                return (
                    <AuthenticatedSignIn
                        setError={props.setError}
                        kioskId={props.kioskId}
                        kioskName={props.kioskName}
                        kioskImageUrl={props.kioskImageUrl}
                        greetingText={getKioskLanguage("swipePageHeaderOne")}
                        mainHeader={getKioskLanguage("swipePageHeaderTwo")}
                        instructionsText={getKioskLanguage("swipePageInstructions")}
                        prospectiveStudentsText={getKioskLanguage("prospectiveStudentsPageHeaderTwo")}
                        showToProspectiveStudents={props.showToProspectiveStudents}
                        goToProspectiveForm={() => setStep(STEPS.PROSPECTIVE_FORM)} />
                );
            case STEPS.CONFIRM_APPOINTMENT:
                return (
                    <ConfirmAppointment
                        studentName={props.studentName}
                        appointmentStartTime={props.appointmentStartTime}
                        instructorName={props.appointmentInstructor.name}
                        instructorUid={props.appointmentInstructor.uid}
                        instructorTitle={props.appointmentInstructor.title}
                        onConfirm={(onConfirmWaitingRoomQueueId) => {
                            props.setWaitingRoomQueueId(onConfirmWaitingRoomQueueId || props.waitingRoomQueueId);
                            setStep(STEPS.CONFIRMATION_PAGE)}}
                        goToSelectService={handleJoinWaitingRoomFromConfirmAppointment}
                        canViewWaitList={canViewWaitList(props.kioskType)}
                        setError={props.setError}
                        setWarning={props.setWarning}
                        email={props.studentEmail}
                        loginId={loginId}
                        studentLastName={studentLastName}
                        kioskId={props.kioskId}
                        studentId={props.studentId}
                        isProspectiveStudent={props.isProspectiveStudent}
                        serviceId={props.service.serviceId} />
                );
            case STEPS.CONFIRMATION_PAGE:
                return (
                    <ConfirmationPage
                        upperText={`You're checked in!`}
                        lowerText={getKioskLanguage("addedToKioskConfirmationPageHeaderTwo")}
                        imageUrl={'../images/starfish/Happy_starfish_darkgray.png'}
                        onTimeout={startOver}
                        isProspectiveStudent={props.isProspectiveStudent}
                        virtualSignInEnabled={props.virtualSignInOptions.virtualSignInEnabled}>
                        {canViewWaitList(props.kioskType) && <StyledButton
                            asLink
                            className={css['linkText']}
                            onClick={() => setStep(STEPS.DISPLAY_WAIT_LIST)}>
                            {'See the waitlist.'}
                        </StyledButton>}
                    </ConfirmationPage>
                );
            case STEPS.DISPLAY_WAIT_LIST:
                return (
                    <WaitList
                        studentId={props.studentId}
                        kioskId={props.kioskId}
                        goToCheckoutConfirmation={(dequeueWarning) => setStep(props.goToCheckoutConfirmation(dequeueWarning))}
                        goToWaitListConfirmation={() => {
                            props.clearNotifications();
                            setStep(STEPS.WAIT_LIST_CONFIRMATION_PAGE);
                        }}
                        setError={props.setError}
                        serviceId={props.service.serviceId}
                        appointmentId={props.appointmentId}
                        waitingRoomQueueId={props.waitingRoomQueueId}
                        setWaitingRoomQueueId={props.setWaitingRoomQueueId}
                        scheduleBlockId={props.scheduleBlockId} />
                );
            case STEPS.PROSPECTIVE_FORM:
                return (
                    <ProspectiveForm
                        kioskId={props.kioskId}
                        kioskUid={props.kioskUid}
                        goBack={goBack}
                        kioskName={props.kioskName}
                        kioskImageUrl={props.kioskImageUrl}
                        prospectsNeedBirthday={props.prospectsNeedBirthday}
                        prospectsNeedPhone={props.prospectsNeedPhone}
                        prospectsNeedZip={props.prospectsNeedZip}
                        setError={props.setError}
                        onSignIn={(signInJson, isProspective) => setStep(props.processSignIn(signInJson, isProspective))}
                        goToProspectiveSignIn={() => setStep(STEPS.PROSPECTIVE_SIGN_IN)}
                        mainHeader={getKioskLanguage("prospectiveStudentsPageHeaderTwo")}
                        contentRef={contentRef}
                        instructionsText={getKioskLanguage("prospectiveStudentsInstructions")} />
                );
            case STEPS.PROSPECTIVE_SIGN_IN:
                return (
                    <ProspectiveSignIn
                        onSignIn={(signInJson, isProspective) => setStep(props.processSignIn(signInJson, isProspective))}
                        setError={props.setError}
                        kioskId={props.kioskId}
                        kioskUid={props.kioskUid}
                        kioskName={props.kioskName}
                        kioskImageUrl={props.kioskImageUrl}
                        greetingText={getKioskLanguage("existingProspectiveStudentPageHeaderOne")}
                        mainHeader={getKioskLanguage("existingProspectiveStudentPageHeaderTwo")}
                        instructionsText={getKioskLanguage("existingProspectiveStudentPageInstructions")}
                        contentRef={contentRef} />
                );
            case STEPS.SELECT_SERVICE:
                return (
                    <ServiceSelection
                        kioskId={props.kioskId}
                        studentId={props.studentId}
                        isProspectiveStudent={props.isProspectiveStudent}
                        studentName={props.studentName}
                        setError={props.setError}
                        selectService={(selectedService, replace = false) => {
                            if (replace) replaceStep(props.selectService(selectedService, replace));
                            else setStep(props.selectService(selectedService, replace));
                        }}
                        startOver={handleStartOver}
                        setWarning={props.setWarning}
                        otherKioskServices={props.otherKioskServices}
                        greetingText={getKioskLanguage("kioskInitiatorPageHeaderOne")}
                        mainHeader={getKioskLanguage("kioskInitiatorPageHeaderTwo")} />
                );
            case STEPS.SELECT_REASON:
                return (
                    <ReasonSelection
                        serviceId={props.service.serviceId}
                        studentId={props.studentId}
                        reason={props.reason}
                        isProspectiveStudent={props.isProspectiveStudent}
                        selectReason={(selectedReason) => setStep(props.selectReason(selectedReason))}
                        setError={props.setError}
                        kioskType={props.kioskType}
                        setWarning={props.setWarning}
                        email={props.studentEmail}
                        showMoreInformationPage={props.showMoreInformationPage}
                        kioskId={props.kioskId}
                        goToConfirmationPage={() => setStep(STEPS.CONFIRMATION_PAGE)}
                        greetingText={getKioskLanguage("visitReasonPageHeaderOne")}
                        mainHeader={getKioskLanguage("visitReasonPageHeaderTwo")}
                        shouldSkipCourseAndMoreInformationPages={props.shouldSkipCourseAndMoreInformationPages()}
                        setWaitingRoomQueueId={props.setWaitingRoomQueueId} />
                );
            case STEPS.SELECT_GROUP:
                return (
                    <GroupSelection
                        onSelectGroupSession={(selectedGroupSession) => setStep(props.selectGroupSession(selectedGroupSession))}
                        studentId={props.studentId}
                        serviceId={props.service.serviceId}
                        setError={props.setError}
                        groupSessionGreetingText={getKioskLanguage("groupSessionJoinPageHeaderOne")}
                        groupSessionMainText={getKioskLanguage("groupSessionJoinPageHeaderTwo")}
                        selectedReasonId={props.reason.id}
                        goToConfirmationPage={() => setStep(STEPS.CONFIRMATION_PAGE)}
                        shouldSkipCourseAndMoreInformationPages={props.shouldSkipCourseAndMoreInformationPages()}
                        kioskId={props.kioskId} />
                );
            case STEPS.SELECT_COURSE:
                return (
                    <CourseSelection
                        studentId={props.studentId}
                        reasonId={props.reason.id}
                        instructorId={props.groupSession.ownerPartyId}
                        serviceId={props.service.serviceId}
                        kioskId={props.kioskId}
                        groupSessionId={props.groupSession.id}
                        groupSessionCourseRestrictions={props.groupSession.courseRestrictions}
                        supplementalInstruction={props.groupSession.supplementalInstruction}
                        supplementalInstructionType={props.groupSession.groupSessionSupplementalInstructionRestriction}
                        course={props.course}
                        selectCourse={(selectedCourse) => setStep(props.selectCourse(selectedCourse))}
                        setError={props.setError}
                        relatedToCoursesPageHeaderOne={getKioskLanguage("relatedToCoursesPageHeaderOne")}
                        relatedToCoursesPageHeaderTwo={getKioskLanguage("relatedToCoursesPageHeaderTwo")}
                        showMoreInformationPage={props.showMoreInformationPage}
                        goToConfirmationPage={() => setStep(STEPS.CONFIRMATION_PAGE)}
                        setWaitingRoomQueueId={props.setWaitingRoomQueueId} />
                );
            case STEPS.CHECKOUT_CONFIRMATION_PAGE:
                return (
                    <ConfirmationPage
                        upperText={getKioskLanguage("checkoutConfirmationPageHeaderOne")}
                        lowerText={getKioskLanguage("checkoutConfirmationPageHeaderTwo")}
                        imageUrl={"../images/starfish/starfish_done.png"}
                        onTimeout={startOver}
                        isProspectiveStudent={props.isProspectiveStudent}
                        virtualSignInEnabled={props.virtualSignInOptions.virtualSignInEnabled} />
                );
            case STEPS.MORE_INFORMATION:
                return (
                    <MoreInformation
                        greetingText={getKioskLanguage("studentFeedbackPageHeaderOne")}
                        mainHeadingText={getKioskLanguage("studentFeedbackPageHeaderTwo")}
                        goToConfirmationPage={() => setStep(STEPS.CONFIRMATION_PAGE)}
                        isProspectiveStudent={props.isProspectiveStudent}
                        serviceId={props.service.serviceId}
                        reasonId={props.reason.id }
                        kioskId={props.kioskId}
                        studentId={props.studentId}
                        courseId={props.course.id}
                        groupSessionId={props.groupSession.id}
                        setError={props.setError}
                        setWaitingRoomQueueId={props.setWaitingRoomQueueId}
                        startOver={startOver}
                        email={props.studentEmail} />
                );
            case STEPS.WAIT_LIST_CONFIRMATION_PAGE:
                return (
                    <ConfirmationPage
                        upperText={getKioskLanguage("stillCheckedInPageHeaderOne")}
                        lowerText={getKioskLanguage("stillCheckedInPageHeaderTwo")}
                        imageUrl={"../images/starfish/starfish_time.png"}
                        onTimeout={startOver}
                        isProspectiveStudent={props.isProspectiveStudent}
                        virtualSignInEnabled={props.virtualSignInOptions.virtualSignInEnabled} />
                );
            case STEPS.CHECKED_IN_OPTIONS:
                return (
                    <CheckedInOptions
                        goToWaitList={() => setStep(STEPS.DISPLAY_WAIT_LIST)}
                        goToWaitListConfirmation={() => setStep(STEPS.WAIT_LIST_CONFIRMATION_PAGE)}
                        goToServiceSelection={() => setStep(STEPS.SELECT_SERVICE)}
                        studentName={props.studentName}
                        studentId={props.studentId}
                        kioskId={props.kioskId}
                        goToCheckoutConfirmation={(dequeueWarning) => setStep(props.goToCheckoutConfirmation(dequeueWarning))}
                        setError={props.setError}
                        setWarning={props.setWarning}
                        serviceId={props.service.serviceId}
                        appointmentId={props.appointmentId}
                        waitingRoomQueueId={props.waitingRoomQueueId}
                        scheduleBlockId={props.scheduleBlockId}
                        waitTime={props.waitTime}
                        kioskType={props.kioskType}
                        isSingleService={props.services.length === 1}
                        mainHeader={getKioskLanguage("checkoutPageHeaderTwo")}
                        virtualSignInForService={authenticatedVirtualSignInForSpecificService()} />
                );
            case STEPS.NO_APPOINTMENT_SCHEDULED:
                return (
                    <ConfirmationPage
                        upperText={getKioskLanguage("appointmentsOnlyPageHeaderOne")}
                        lowerText={getKioskLanguage("appointmentsOnlyPageHeaderTwo")}
                        imageUrl={'../images/starfish/calendar_icon.png'}
                        onTimeout={startOver}
                        timeout={10000}
                        isProspectiveStudent={props.isProspectiveStudent}
                        virtualSignInEnabled={props.virtualSignInOptions.virtualSignInEnabled} />
                );
            case STEPS.SELECT_MEETING_TYPE_PAGE:
                return (
                    <SelectMeetingType
                        sessionGroupText={"I want to sign up for a group session."}
                        oneOnOneText={"I want to meet with someone one-on-one."}
                        instructionsText={getKioskLanguage("selectMeetingTypePageInstructions")}
                        onSelectSessionGroup={() => setStep(STEPS.SELECT_GROUP)}
                        onSelectOneOnOne={() => setStep(props.selectOneOnOne())}
                        kioskId={props.kioskId}
                        reasonId={props.reason.id}
                        serviceId={props.service.serviceId}
                        setError={props.setError}
                        shouldSkipCourseAndMoreInformationPages={props.shouldSkipCourseAndMoreInformationPages()}
                        studentId={props.studentId}
                        goToConfirmationPage={() => setStep(STEPS.CONFIRMATION_PAGE)} />
                );
            case STEPS.MULTIPLE_CHECKINS_OPTION:
                return (
                    <MultipleCheckInsOption 
                        setWarning={props.setWarning}
                        greetingText={getKioskLanguage("checkIntoAnotherQueuePageHeaderOne")}
                        instructionsText={getKioskLanguage("checkIntoAnotherQueuePageInstructions")}
                        startOver={handleStartOver}
                        onConfirmMultipleCheckIn={() => setStep(props.onConfirmMultipleCheckIn())}
                        allServices={props.services.concat(props.otherKioskServices)}
                        kioskId={props.kioskId}
                        kioskType={props.kioskType}
                        kioskName={props.kioskName}
                        virtualSignInEnabled={props.virtualSignInOptions.virtualSignInEnabled} />
                );
        }
    };

    const getFooter = () => 
        (!props.virtualSignInOptions.virtualSignInEnabled || step !== props.initialStep) && step.showFooter && canGoBack 
            ? <KioskFooter startOver={handleStartOver} goBack={() => {
                props.deleteLastVisitedServiceWithAppointment(step);
                goBack();
            }} /> 
            : null;

    const getError = () => props.error
        ? <ErrorMessage message={props.error} />
        : null;

    const getWarning = () => props.warning
        ? <WarningMessage message={props.warning} onDismiss={props.setWarning} />
        : null;

    const getHeader = () => step.showHeader
        ?  <KioskHeader
            name={props.service && props.displayServiceName(props.service) ? props.displayServiceName(props.service) : props.kioskName}
            serviceUid={props.service && props.service.serviceUid ? props.service.serviceUid : props.kioskUid }
            smallImage={true}
            showWaitTime={props.service && step.showWaitTme && canViewWaitList(props.kioskType)}
            serviceId={props.service.serviceId}
            studentId={props.studentId}
            isProspectiveStudent={props.isProspectiveStudent}
            email={props.studentEmail}
            kioskId={props.kioskId}
            setError={props.setError} />
        : null;

    const getConfirmActivity = () => step.checkActivity && 
        (!props.virtualSignInOptions.virtualSignInEnabled || props.isProspectiveStudent)
        ? <ConfirmActivity
            startOver={startOver}
            descriptionText={"You've been inactive for two minutes. The session will end in 20 seconds."}
            timeoutDurationInSeconds={20} />
        : null;

    const getContent = () => (
        <>
            {getError()}
            {getWarning()}
            {getHeader()}
            {getStep()}
            {getConfirmActivity()}
        </>
    );

    return <StickyFooterLayout content={getContent()} footer={getFooter()} ref={contentRef} />
}

Kiosk.propTypes = {
    appointmentId: PropTypes.number,
    appointmentInstructor: AppointmentInstructor,
    appointmentStartTime: PropTypes.string,
    clearNotifications: PropTypes.func,
    course: KioskCourseOption,
    customLanguage: KioskCustomLanguage,
    deleteLastVisitedServiceWithAppointment: PropTypes.func.isRequired,
    displayServiceName: PropTypes.func.isRequired,
    error: PropTypes.string,
    goToCheckoutConfirmation: PropTypes.func.isRequired,
    groupSession: KioskGroupSession,
    initialStep: PropTypes.object,
    isProspectiveStudent: PropTypes.bool.isRequired,
    kioskId: PropTypes.number.isRequired,
    kioskImageUrl: PropTypes.string.isRequired,
    kioskName: PropTypes.string.isRequired,
    kioskType: PropTypes.string.isRequired,
    kioskUid: PropTypes.string.isRequired,
    lastNameField: PropTypes.string.isRequired,
    lastNameSignOnDisabled: PropTypes.bool.isRequired,
    loadActiveXListener: PropTypes.func,
    onConfirmMultipleCheckIn: PropTypes.func.isRequired,
    otherKioskServices: PropTypes.arrayOf(KioskService),
    processSignIn: PropTypes.func.isRequired,
    prospectsNeedBirthday: PropTypes.bool,
    prospectsNeedPhone: PropTypes.bool,
    prospectsNeedZip: PropTypes.bool,
    reason: KioskAppointmentReason,
    scheduleBlockId: PropTypes.number,
    service: KioskService,
    services: PropTypes.arrayOf(KioskService),
    selectCourse: PropTypes.func.isRequired,
    selectGroupSession: PropTypes.func.isRequired,
    selectOneOnOne: PropTypes.func.isRequired,
    selectReason: PropTypes.func.isRequired,
    selectService: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    setServiceWithAppointmentAsVisited: PropTypes.func.isRequired,
    setWaitingRoomQueueId: PropTypes.func.isRequired,
    setWarning: PropTypes.func.isRequired,
    shouldSkipCourseAndMoreInformationPages: PropTypes.func.isRequired,
    showCoursePage: PropTypes.bool.isRequired,
    showMoreInformationPage: PropTypes.bool.isRequired,
    showToProspectiveStudents: PropTypes.bool.isRequired,
    studentEmail: PropTypes.string,
    studentId: SFEntityID,
    studentIdField: PropTypes.string.isRequired,
    studentName: PropTypes.string,
    swipeCardConfigs: SwipeCardConfigs.isRequired,
    unloadActiveXListener: PropTypes.func,
    updateActiveXLastName: PropTypes.func,
    virtualSignInOptions: VirtualSignInOptions.isRequired,
    waitingRoomQueueId: PropTypes.number,
    waitTime: PropTypes.number,
    warning: PropTypes.string
};

export default Kiosk;
